import { api } from '../slices/api';

export interface WorkflowStep {
	name: string;
	initials: string | null;
	date: string | null;
}

export interface OutakeRow {
	employee_initials: string | null;
	quantity_out: string | null;
	date: string | null;
}

export interface IntakeRow {
	employee_initials: string | null;
	quantity_in: string | null;
	date: string | null;
}

export interface ErpStep {
	label: string;
	user: string | null;
	at: string | null;
}

export interface ErpIntakeEvent {
	operator_name: string | null;
	initials: string | null;
	quantity: string | null;
	date: string | null;
}

export interface TravelerErpData {
	order_line_item_id: string;
	production_method: string;
	steps: Record<string, ErpStep>;
	intake_events: ErpIntakeEvent[];
	outake_events: ErpIntakeEvent[];
	recovered_fields?: string[];
	material_cert_not_applicable?: boolean;
	material_cert_not_available?: {
		by: string | null;
		by_name?: string | null;
		at: string | null;
	} | null;
	awaiting_packet_review?: boolean;
}

export interface TravelerData {
	work_order: string | null;
	part_number: string | null;
	revision: string | null;
	quantity: number | null;
	due_date: string | null;
	workflow: WorkflowStep[];
	outake: OutakeRow[];
	intake: IntakeRow[];
	erp?: TravelerErpData;
}

export interface FaiInspector {
	initials: string;
	date: string;
}

export interface FaiTool {
	serial: string | null;
	calibration_status: string | null;
	calibration_due_date: string | null;
	has_calibration_certificate: boolean | null;
}

export interface GageUsed {
	serial: string;
	calibration_status: string | null;
	calibration_due_date: string | null;
	has_calibration_certificate: boolean | null;
}

export interface FaiMeasurement {
	id: number;
	nominal: string | null;
	minus: string | null;
	plus: string | null;
	lcl: string | null;
	ucl: string | null;
	visual: boolean;
	gage_type_name?: string | null;
	measurement_strategy?: string | null;
	samples: string[];
	inspectors: FaiInspector[];
	tools?: FaiTool[];
}

export interface FaiData {
	order_number: string | null;
	sample_columns: number | null;
	job_due_date?: string | null;
	measurements: FaiMeasurement[];
	gages_used?: GageUsed[];
}

export type DocumentType = 'traveler' | 'packet' | 'unknown';
export type AuditStatus =
	| 'pass'
	| 'review'
	| 'fail'
	| 'complete'
	| 'printed'
	| 'internal';

export type PacketReviewTableFilters = {
	search?: string;
	issueCategories?: string[];
	lookoutsOnly?: boolean;
	failuresOnly?: boolean;
};

function packetReviewFilterQuery({
	statuses,
	search,
	issueCategories,
	lookoutsOnly,
	failuresOnly,
}: PacketReviewTableFilters & { statuses?: AuditStatus[] }) {
	const params = new URLSearchParams();
	if (statuses?.length) params.set('statuses', statuses.join(','));
	if (search) params.set('search', search);
	if (issueCategories?.length)
		params.set('issue_category', issueCategories.join(','));
	if (lookoutsOnly) params.set('lookouts_only', 'true');
	if (failuresOnly) params.set('failures_only', 'true');
	return params;
}

function packetReviewFilterBody({
	search,
	issueCategories,
	lookoutsOnly,
	failuresOnly,
}: PacketReviewTableFilters) {
	return {
		search,
		issue_category: issueCategories?.join(','),
		lookouts_only: lookoutsOnly || undefined,
		failures_only: failuresOnly || undefined,
	};
}
export type AuditSource = 'upload' | 'erp';
export type RunStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface OverlayStep {
	user: string | null;
	at: string | null;
}

/** Header fields projected onto a slim document row. */
export interface AuditDocumentHeader {
	work_order: string | null;
	part_number: string | null;
	revision: string | null;
	quantity: number | null;
	due_date: string | null;
	recovered_fields?: string[];
	order_accepted?: OverlayStep;
	icf_operator?: OverlayStep;
	machining_complete?: OverlayStep;
	traveler_completed?: OverlayStep;
}

export type CompletionDateMode =
	| 'today'
	| 'pb_intake'
	| 'next_business_after_intake';

export interface RecoveryEvent {
	id: string;
	layer: string;
	line_item_id: string | null;
	line_item_name: string | null;
	order_id: string | null;
	work_order: string | null;
	field_key: string;
	field_label?: string;
	method: string;
	method_label?: string;
	before: unknown;
	after: unknown;
	before_display?: string;
	after_display?: string;
	applied_by: string | null;
	created_time: string;
}

export interface RecoveryPreviewResult {
	id: string;
	ok?: boolean;
	filename?: string | null;
	error?: string | null;
	events: Array<{
		field_key: string;
		method: string;
		before: unknown;
		after: unknown;
	}>;
	prompts: Array<{
		type: string;
		reports?: KeyenceCandidate[];
		extracted?: string;
	}>;
	status: AuditStatus;
	failures: string[];
	lookouts: string[];
}

export interface KeyenceCandidate {
	id: string;
	view: string | null;
	measurement_time: string | null;
	created_time: string | null;
}

export interface ApproveResult {
	results: Array<{
		id: string;
		ok: boolean;
		filename?: string | null;
		work_order?: string | null;
		error?: string;
		clamped_to_icf?: boolean;
		completed_at?: string;
		not_approvable?: boolean;
		refusal_code?: string;
		incomplete_tasks?: string[];
	}>;
	print_job_ids: string[];
	print_failed_ids?: string[];
	print_errors?: PrintJobErrorSummary[];
}

export interface CompletePriorTasksResult {
	id: string;
	ok: boolean;
	filename?: string | null;
	work_order?: string | null;
	error?: string;
	refusal_code?: string;
	changed_fields?: string[];
	status?: AuditStatus;
}

export interface PrintJobErrorSummary {
	id: string;
	error: string;
	next_step?: string;
}

export interface PacketPrintErrorDetails {
	code?: string;
	exception_class?: string;
	next_step?: string;
	line_items?: Array<{
		id?: string;
		label?: string;
		missing?: string[];
		why?: string;
	}>;
}

export interface PacketPrintJob {
	id: string;
	run: string | null;
	parent: string | null;
	page_count: number;
	line_item_ids: string[];
	status: 'queued' | 'printed' | 'reprint' | 'failed';
	error?: string | null;
	error_details?: PacketPrintErrorDetails | null;
	confirmed_by: string | null;
	confirmed_at: string | null;
	created_time: string;
	modified_time: string;
	created_by: string | null;
}

export interface RecoverOptions {
	material_cert_na?: boolean;
	revision?: string;
	keyence_report_id?: string;
	keyence_operator?: string;
	keyence_at?: string;
}

/** Slim row returned by the paginated documents endpoint. */
export interface AuditDocumentListItem {
	id: string;
	/** Parent sales-order id when the row is ERP-backed (for Work Order links). */
	order: string | null;
	order_line_item: string | null;
	filename: string;
	document_type: DocumentType;
	status: AuditStatus;
	failures: string[];
	lookouts: string[];
	header: AuditDocumentHeader;
	has_fai: boolean;
}

/** Full document payload from the document-detail endpoint. */
export interface AuditDocument {
	id: string;
	order_line_item: string | null;
	filename: string;
	document_type: DocumentType;
	status: AuditStatus;
	traveler_data: TravelerData | null;
	fai_data: FaiData | null;
	failures: string[];
	lookouts: string[];
	parse_error: string;
}

export interface ManualEditIssue {
	text: string;
	severity: 'fail' | 'review';
}

export interface ManualEditStep {
	key: string;
	label: string;
	user: string | null;
	/** ISO datetime. */
	at: string | null;
	/** Central calendar day (``YYYY-MM-DD``) of ``at``, the day validation uses. */
	day: string | null;
	/** ``at`` as stored when it is present but cannot be read as a date. */
	raw_at: string | null;
	issues: ManualEditIssue[];
}

export interface ManualEditInspector {
	/** Position in the balloon's ``inspectors`` list (the edit key). */
	index: number;
	/** 1-based sample this sign-off belongs to; null when there is no recorded sample. */
	sample_number: number | null;
	value: string | null;
	initials: string | null;
	/** Usually ``MM/DD/YY``; older recoveries stored ``YYYY-MM-DD``. */
	date: string | null;
}

export interface ManualEditBalloon {
	balloon: string;
	visual: boolean;
	/** False when the balloon id is not unique, so an edit could land on the wrong one. */
	editable: boolean;
	issues: ManualEditIssue[];
	inspectors: ManualEditInspector[];
}

/** Every editable packet field for the manual recovery screen. */
export interface ManualEditForm {
	id: string;
	filename: string;
	status: AuditStatus;
	failures: string[];
	lookouts: string[];
	has_layer: boolean;
	editable: boolean;
	locked_reason: string | null;
	steps: ManualEditStep[];
	revision: {
		value: string | null;
		part_number_rev: string | null;
		issues: ManualEditIssue[];
	};
	material_cert: {
		has_certificate: boolean | null;
		not_applicable: boolean;
		issues: ManualEditIssue[];
	};
	fai: ManualEditBalloon[];
	other_issues: ManualEditIssue[];
	/** Present on the POST response: how many fields were written. */
	changed?: number;
}

/** ``step.<key>`` -> {user, at}; ``fai.inspector.<balloon>.<index>`` -> {initials, date}. */
export interface ManualEdit {
	field_key: string;
	value: unknown;
	/** What the screen showed; the server refuses with 409 when the packet has moved on. */
	expected?: unknown;
}

export interface AuditRunSummary {
	pass: number;
	review: number;
	fail: number;
	complete?: number;
	printed?: number;
	internal?: number;
	truncated?: boolean;
	truncated_at?: number;
}

/** Run header only -- documents are fetched via the paginated endpoint. */
export interface AuditRun {
	id: string;
	created_time: string;
	source: AuditSource;
	status: RunStatus;
	criteria: ErpRunCriteria;
	error: string;
	file_count: number;
	summary: AuditRunSummary;
}

export type AuditRunListItem = Omit<AuditRun, 'error'>;

/**
 * Which line items a run sweeps. A job waits in Delivered In Full for its
 * packet review and only reaches Completed once someone signs it off, so
 * 'awaiting_review' is the queue and 'completed' is the retrospective check.
 */
export type ErpRunPopulation = 'completed' | 'awaiting_review' | 'both';

export interface ErpRunCriteria {
	accepted_date_from?: string;
	accepted_date_to?: string;
	/** @deprecated Legacy Live ERP criteria key; prefer accepted_date_from. */
	ship_date_from?: string;
	/** @deprecated Legacy Live ERP criteria key; prefer accepted_date_to. */
	ship_date_to?: string;
	created_after?: string;
	production_method?: string;
	issues_only?: boolean;
	population?: ErpRunPopulation;
}

export type DocumentOrdering =
	| 'severity'
	| '-severity'
	| 'filename'
	| '-filename';

export interface DocumentsPageParams {
	runId: string;
	status?: AuditStatus | 'all';
	lookoutsOnly?: boolean;
	failuresOnly?: boolean;
	faiOnly?: boolean;
	documentType?: DocumentType | 'all';
	search?: string;
	issueCategories?: string[];
	ordering?: DocumentOrdering;
	page?: number;
	pageSize?: number;
}

export interface PaginatedDocuments {
	count: number;
	next: string | null;
	previous: string | null;
	results: AuditDocumentListItem[];
}

export interface IssueBreakdownItem {
	key: string;
	label: string;
	/** Documents with at least one issue in this category (drives the chips). */
	count: number;
	/** Total failure instances in this category (drives the report chart). */
	failure_count: number;
	/** Total lookout instances in this category (drives the report chart). */
	lookout_count: number;
}

export interface IssueBreakdownTotals {
	failures: number;
	lookouts: number;
	documents_with_issues: number;
}

export interface IssueBreakdown {
	breakdown: IssueBreakdownItem[];
	totals: IssueBreakdownTotals;
}

export const travelerAuditApi = api.injectEndpoints({
	endpoints: (builder) => ({
		createAuditRun: builder.mutation<AuditRun, FormData>({
			query: (formData) => ({
				url: 'traveler-audit/runs/',
				method: 'POST',
				body: formData,
			}),
			invalidatesTags: ['TravelerAuditRun'],
		}),
		createErpRun: builder.mutation<AuditRun, ErpRunCriteria>({
			query: (criteria) => ({
				url: 'traveler-audit/runs/erp/',
				method: 'POST',
				body: criteria,
			}),
			invalidatesTags: ['TravelerAuditRun'],
		}),
		getAuditRuns: builder.query<AuditRunListItem[], void>({
			query: () => 'traveler-audit/runs/',
			providesTags: ['TravelerAuditRun'],
		}),
		deleteAuditRun: builder.mutation<void, string>({
			query: (id) => ({
				url: `traveler-audit/runs/${id}/`,
				method: 'DELETE',
			}),
			invalidatesTags: ['TravelerAuditRun'],
		}),
		getAuditRun: builder.query<AuditRun, string>({
			// Do not send Cache-Control/Pragma: staging CORS allow-headers omit
			// them, so the browser blocks the GET ("Failed to load run"). Stale
			// pending headers are avoided via loadRun(id, false) force-refetch.
			query: (id) => `traveler-audit/runs/${id}/`,
			providesTags: (_result, _err, id) => [
				{ type: 'TravelerAuditRun', id },
				'TravelerAuditRun',
			],
		}),
		getAuditRunDocuments: builder.query<
			PaginatedDocuments,
			DocumentsPageParams
		>({
			query: ({
				runId,
				status,
				lookoutsOnly,
				failuresOnly,
				faiOnly,
				documentType,
				search,
				issueCategories,
				ordering,
				page,
				pageSize,
			}) => {
				const params = new URLSearchParams();
				if (status && status !== 'all') params.set('status', status);
				if (lookoutsOnly) params.set('lookouts_only', 'true');
				if (failuresOnly) params.set('failures_only', 'true');
				if (faiOnly) params.set('fai_only', 'true');
				if (documentType && documentType !== 'all')
					params.set('document_type', documentType);
				if (search) params.set('search', search);
				if (issueCategories && issueCategories.length)
					params.set('issue_category', issueCategories.join(','));
				if (ordering) params.set('ordering', ordering);
				if (page) params.set('page', String(page));
				if (pageSize) params.set('page_size', String(pageSize));
				const qs = params.toString();
				return `traveler-audit/runs/${runId}/documents/${qs ? `?${qs}` : ''}`;
			},
			providesTags: (_result, _err, arg) => [
				{ type: 'TravelerAuditRun', id: arg.runId },
				'TravelerAuditRun',
			],
		}),
		getIssueBreakdown: builder.query<IssueBreakdown, string>({
			query: (runId) => `traveler-audit/runs/${runId}/issue-breakdown/`,
			providesTags: (_result, _err, runId) => [
				{ type: 'TravelerAuditRun', id: runId },
				'TravelerAuditRun',
			],
		}),
		getAuditDocument: builder.query<
			AuditDocument,
			{ runId: string; docId: string }
		>({
			query: ({ runId, docId }) =>
				`traveler-audit/runs/${runId}/documents/${docId}/`,
			providesTags: (_result, _err, arg) => [
				{ type: 'TravelerAuditRun', id: arg.runId },
				'TravelerAuditRun',
			],
		}),
		exportAuditRun: builder.query<Blob, string>({
			query: (id) => ({
				url: `traveler-audit/runs/${id}/export/`,
				responseHandler: async (response) => await response.blob(),
			}),
		}),
		getMatchingDocumentIds: builder.query<
			{ ids: string[] },
			PacketReviewTableFilters & { runId: string; statuses?: AuditStatus[] }
		>({
			query: ({ runId, ...filters }) => {
				const qs = packetReviewFilterQuery(filters).toString();
				return `traveler-audit/runs/${runId}/matching-ids/${
					qs ? `?${qs}` : ''
				}`;
			},
		}),
		approveDocuments: builder.mutation<
			ApproveResult,
			PacketReviewTableFilters & {
				runId: string;
				documentIds?: string[];
				selectAllMatching?: boolean;
				statuses?: AuditStatus[];
				mode: CompletionDateMode;
				print?: boolean;
			}
		>({
			query: ({
				runId,
				documentIds,
				selectAllMatching,
				statuses,
				mode,
				print,
				search,
				issueCategories,
				lookoutsOnly,
				failuresOnly,
			}) => ({
				url: `traveler-audit/runs/${runId}/approve/`,
				method: 'POST',
				body: {
					document_ids: documentIds,
					select_all_matching: selectAllMatching,
					statuses,
					mode,
					print: print !== false,
					...packetReviewFilterBody({
						search,
						issueCategories,
						lookoutsOnly,
						failuresOnly,
					}),
				},
			}),
			invalidatesTags: (_result, _err, arg) => [
				{ type: 'TravelerAuditRun', id: arg.runId },
				'TravelerAuditRun',
				'TravelerAuditPrintJob',
				'TravelerAuditRecovery',
			],
		}),
		previewRecovery: builder.mutation<
			{ results: RecoveryPreviewResult[] },
			{ runId: string; documentIds: string[]; options?: RecoverOptions }
		>({
			query: ({ runId, documentIds, options }) => ({
				url: `traveler-audit/runs/${runId}/recover-preview/`,
				method: 'POST',
				body: { document_ids: documentIds, ...options },
			}),
		}),
		recoverDocuments: builder.mutation<
			{ results: RecoveryPreviewResult[] },
			PacketReviewTableFilters & {
				runId: string;
				documentIds?: string[];
				selectAllMatching?: boolean;
				options?: RecoverOptions;
			}
		>({
			query: ({
				runId,
				documentIds,
				selectAllMatching,
				options,
				search,
				issueCategories,
				lookoutsOnly,
				failuresOnly,
			}) => ({
				url: `traveler-audit/runs/${runId}/recover/`,
				method: 'POST',
				body: {
					document_ids: documentIds,
					select_all_matching: selectAllMatching,
					statuses: ['fail', 'review'],
					...packetReviewFilterBody({
						search,
						issueCategories,
						lookoutsOnly,
						failuresOnly,
					}),
					...options,
				},
			}),
			invalidatesTags: (_result, _err, arg) => [
				{ type: 'TravelerAuditRun', id: arg.runId },
				'TravelerAuditRun',
				'TravelerAuditRecovery',
			],
		}),
		rollbackDocuments: builder.mutation<
			{ results: Array<{ id: string; ok: boolean; error?: string }> },
			{ runId: string; documentIds: string[]; fieldKey?: string }
		>({
			query: ({ runId, documentIds, fieldKey }) => ({
				url: `traveler-audit/runs/${runId}/rollback/`,
				method: 'POST',
				body: { document_ids: documentIds, field_key: fieldKey },
			}),
			invalidatesTags: (_result, _err, arg) => [
				{ type: 'TravelerAuditRun', id: arg.runId },
				'TravelerAuditRun',
				'TravelerAuditRecovery',
			],
		}),
		getManualEditForm: builder.query<
			ManualEditForm,
			{ runId: string; documentId: string }
		>({
			query: ({ runId, documentId }) => ({
				url: `traveler-audit/runs/${runId}/manual-edit/`,
				params: { document_id: documentId },
			}),
			providesTags: (_result, _err, arg) => [
				{ type: 'TravelerAuditRun', id: arg.runId },
				'TravelerAuditRun',
			],
		}),
		manualEditDocument: builder.mutation<
			ManualEditForm,
			{ runId: string; documentId: string; edits: ManualEdit[] }
		>({
			query: ({ runId, documentId, edits }) => ({
				url: `traveler-audit/runs/${runId}/manual-edit/`,
				method: 'POST',
				body: { document_id: documentId, edits },
			}),
			invalidatesTags: (_result, _err, arg) => [
				{ type: 'TravelerAuditRun', id: arg.runId },
				'TravelerAuditRun',
				'TravelerAuditRecovery',
			],
		}),
		completePriorTasks: builder.mutation<
			{ results: CompletePriorTasksResult[] },
			{ runId: string; documentIds: string[] }
		>({
			query: ({ runId, documentIds }) => ({
				url: `traveler-audit/runs/${runId}/complete-prior-tasks/`,
				method: 'POST',
				body: { document_ids: documentIds },
			}),
			invalidatesTags: (_result, _err, arg) => [
				{ type: 'TravelerAuditRun', id: arg.runId },
				'TravelerAuditRun',
				'TravelerAuditRecovery',
			],
		}),
		returnToReview: builder.mutation<
			{ results: Array<{ id: string; ok: boolean; error?: string }> },
			{ runId: string; documentIds: string[] }
		>({
			query: ({ runId, documentIds }) => ({
				url: `traveler-audit/runs/${runId}/return-to-review/`,
				method: 'POST',
				body: { document_ids: documentIds },
			}),
			invalidatesTags: (_result, _err, arg) => [
				{ type: 'TravelerAuditRun', id: arg.runId },
				'TravelerAuditRun',
				'TravelerAuditRecovery',
			],
		}),
		getRecoveryEvents: builder.query<RecoveryEvent[], string>({
			query: (runId) => `traveler-audit/runs/${runId}/recovery-events/`,
			providesTags: ['TravelerAuditRecovery'],
		}),
		getPrintJobs: builder.query<PacketPrintJob[], string | void>({
			query: (runId) =>
				runId
					? `traveler-audit/runs/${runId}/print-jobs/`
					: 'traveler-audit/print-jobs/',
			providesTags: ['TravelerAuditPrintJob'],
		}),
		confirmPrintJob: builder.mutation<PacketPrintJob, string>({
			query: (id) => ({
				url: `traveler-audit/print-jobs/${id}/confirm/`,
				method: 'POST',
			}),
			invalidatesTags: ['TravelerAuditPrintJob', 'TravelerAuditRun'],
		}),
		printCompletedDocuments: builder.mutation<
			{
				print_job_ids: string[];
				print_failed_ids?: string[];
				print_errors?: PrintJobErrorSummary[];
			},
			PacketReviewTableFilters & {
				runId: string;
				documentIds?: string[];
				selectAllMatching?: boolean;
			}
		>({
			query: ({
				runId,
				documentIds,
				selectAllMatching,
				search,
				issueCategories,
				lookoutsOnly,
				failuresOnly,
			}) => ({
				url: `traveler-audit/runs/${runId}/print-completed/`,
				method: 'POST',
				body: {
					document_ids: documentIds,
					select_all_matching: selectAllMatching,
					statuses: ['complete'],
					...packetReviewFilterBody({
						search,
						issueCategories,
						lookoutsOnly,
						failuresOnly,
					}),
				},
			}),
			invalidatesTags: ['TravelerAuditPrintJob', 'TravelerAuditRun'],
		}),
		setPrintStatus: builder.mutation<
			AuditDocument,
			{ runId: string; documentId: string; status: 'complete' | 'printed' }
		>({
			query: ({ runId, documentId, status }) => ({
				url: `traveler-audit/runs/${runId}/set-print-status/`,
				method: 'POST',
				body: { document_id: documentId, status },
			}),
			invalidatesTags: (_result, _err, arg) => [
				{ type: 'TravelerAuditRun', id: arg.runId },
				'TravelerAuditRun',
			],
		}),
		reprintPrintJob: builder.mutation<PacketPrintJob, string>({
			query: (id) => ({
				url: `traveler-audit/print-jobs/${id}/reprint/`,
				method: 'POST',
			}),
			invalidatesTags: ['TravelerAuditPrintJob'],
		}),
		retryPrintJob: builder.mutation<PacketPrintJob, string>({
			query: (id) => ({
				url: `traveler-audit/print-jobs/${id}/retry/`,
				method: 'POST',
			}),
			invalidatesTags: ['TravelerAuditPrintJob'],
		}),
		getPrintJobPdf: builder.query<Blob, string>({
			query: (id) => ({
				url: `traveler-audit/print-jobs/${id}/pdf/`,
				responseHandler: async (response) => await response.blob(),
			}),
		}),
	}),
});

export const {
	useCreateAuditRunMutation,
	useCreateErpRunMutation,
	useDeleteAuditRunMutation,
	useGetAuditRunsQuery,
	useGetAuditRunQuery,
	useLazyGetAuditRunQuery,
	useGetAuditRunDocumentsQuery,
	useGetIssueBreakdownQuery,
	useGetAuditDocumentQuery,
	useLazyGetAuditDocumentQuery,
	useLazyExportAuditRunQuery,
	useLazyGetMatchingDocumentIdsQuery,
	useApproveDocumentsMutation,
	usePrintCompletedDocumentsMutation,
	useSetPrintStatusMutation,
	usePreviewRecoveryMutation,
	useRecoverDocumentsMutation,
	useRollbackDocumentsMutation,
	useGetManualEditFormQuery,
	useManualEditDocumentMutation,
	useCompletePriorTasksMutation,
	useReturnToReviewMutation,
	useGetRecoveryEventsQuery,
	useGetPrintJobsQuery,
	useLazyGetPrintJobsQuery,
	useConfirmPrintJobMutation,
	useReprintPrintJobMutation,
	useRetryPrintJobMutation,
	useLazyGetPrintJobPdfQuery,
} = travelerAuditApi;
